import React, { useState, useEffect } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../utils/currency';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://store.comparehomeupgrades.com/api';

const STATUS_COLORS = {
  'Active':     'bg-green-100 text-green-700',
  'Inactive':   'bg-yellow-100 text-yellow-700',
  'Incomplete': 'bg-red-100 text-red-700',
  'Unknown':    'bg-gray-100 text-gray-500',
};

export function AmazonProductList({ storeId }) {
  const [products, setProducts]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [syncing, setSyncing]           = useState(false);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [channelFilter, setChannelFilter] = useState('All'); // 'All' | 'FBM' | 'FBA'
  const [editingCost, setEditingCost]   = useState(null);
  const [costValue, setCostValue]       = useState('');

  useEffect(() => { if (storeId) fetchProducts(); }, [storeId]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API_BASE_URL}/amazon/${storeId}/products`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) setProducts(data.products);
      else toast.error('Failed to load products');
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API_BASE_URL}/amazon/${storeId}/products/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) { toast.success(`Synced ${data.synced} products!`); fetchProducts(); }
      else toast.error('Sync failed');
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  const handleUpdateCost = async (productId) => {
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API_BASE_URL}/amazon/${storeId}/products/${productId}/cost`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ cost_price: parseFloat(costValue) })
      });
      const data = await res.json();
      if (data.success) { toast.success('Cost updated!'); setEditingCost(null); fetchProducts(); }
      else toast.error('Failed to update cost');
    } catch { toast.error('Failed to update cost'); }
  };

  const filteredProducts = products.filter(p => {
    const q = search.toLowerCase();
    const matchesSearch =
      p.product_name?.toLowerCase().includes(q) ||
      p.seller_sku?.toLowerCase().includes(q)    ||
      p.asin?.toLowerCase().includes(q);
    const matchesStatus  = statusFilter === 'All' || (p.listing_status || 'Unknown') === statusFilter;
    const matchesChannel = channelFilter === 'All'
      || (channelFilter === 'FBA' && p.fulfillment_channel === 'AFN')
      || (channelFilter === 'FBM' && p.fulfillment_channel !== 'AFN');
    return matchesSearch && matchesStatus && matchesChannel;
  });

  const fbaCount = products.filter(p => p.fulfillment_channel === 'AFN').length;
  const fbmCount = products.filter(p => p.fulfillment_channel !== 'AFN').length;

  const statusCounts = filteredProducts.reduce((acc, p) => {
    const s = p.listing_status || 'Unknown';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Amazon Products</h1>
          <p className="text-sm text-gray-500">{products.length} total · {filteredProducts.length} shown</p>
        </div>
        <button onClick={handleSync} disabled={syncing}
          className="flex items-center px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:from-orange-700 hover:to-orange-800 transition-all shadow disabled:opacity-50 text-sm">
          <RefreshCw className={`mr-2 ${syncing ? 'animate-spin' : ''}`} size={16} />
          {syncing ? 'Syncing...' : 'Sync from Amazon'}
        </button>
      </div>

      {/* FBA / FBM channel filter */}
      <div className="flex gap-2 mb-3">
        {[
          { label: `All (${products.length})`,  value: 'All' },
          { label: `FBM (${fbmCount})`,          value: 'FBM' },
          { label: `FBA (${fbaCount})`,          value: 'FBA' },
        ].map(c => (
          <button key={c.value} onClick={() => setChannelFilter(c.value)}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              channelFilter === c.value
                ? c.value === 'FBA' ? 'bg-blue-600 text-white'
                : c.value === 'FBM' ? 'bg-orange-600 text-white'
                : 'bg-gray-700 text-white'
                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
            }`}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Status filter */}
      <div className="flex gap-2 mb-3">
        {['All', 'Active', 'Inactive', 'Incomplete'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === s ? 'bg-orange-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
            }`}>
            {s} ({s === 'All' ? filteredProducts.length : statusCounts[s] || 0})
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow p-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by product name, SKU, or ASIN..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-orange-500" size={32} /></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="text-left py-3 px-3 text-gray-600 font-semibold">Product</th>
                <th className="text-left py-3 px-2 text-gray-600 font-semibold">ASIN</th>
                <th className="text-left py-3 px-2 text-gray-600 font-semibold">SKU</th>
                <th className="text-center py-3 px-2 text-gray-600 font-semibold">Channel</th>
                <th className="text-center py-3 px-2 text-gray-600 font-semibold">Status</th>
                <th className="text-right py-3 px-2 text-gray-600 font-semibold">Price</th>
                <th className="text-right py-3 px-2 text-gray-600 font-semibold">Cost</th>
                <th className="text-center py-3 px-2 text-gray-600 font-semibold">Stock</th>
                <th className="text-right py-3 px-3 text-gray-600 font-semibold">Margin</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id} className="border-b border-gray-100 hover:bg-orange-50 transition-colors">
                  <td className="py-2 px-3">
                    <p className="font-medium text-gray-800 truncate max-w-xs" title={product.product_name}>{product.product_name}</p>
                  </td>
                  <td className="py-2 px-2 font-mono text-xs text-gray-600">{product.asin || '—'}</td>
                  <td className="py-2 px-2 text-xs text-gray-600">
                    <span className="truncate block max-w-[100px]" title={product.seller_sku}>{product.seller_sku || '—'}</span>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${product.fulfillment_channel === 'AFN' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                      {product.fulfillment_channel === 'AFN' ? 'FBA' : 'FBM'}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[product.listing_status] || STATUS_COLORS['Unknown']}`}>
                      {product.listing_status || 'Unknown'}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-right font-semibold text-orange-600">{formatCurrency(product.price)}</td>
                  <td className="py-2 px-2 text-right">
                    {editingCost === product.id ? (
                      <input type="number" step="0.01" value={costValue}
                        onChange={(e) => setCostValue(e.target.value)}
                        onBlur={() => handleUpdateCost(product.id)}
                        onKeyPress={(e) => e.key === 'Enter' && handleUpdateCost(product.id)}
                        className="w-16 px-1 py-0.5 border border-orange-500 rounded text-right text-xs" autoFocus />
                    ) : (
                      <button onClick={() => { setEditingCost(product.id); setCostValue(product.cost_price || '0'); }}
                        className="text-gray-700 hover:text-orange-600 font-medium text-xs">
                        {formatCurrency(product.cost_price || 0)}
                      </button>
                    )}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      product.quantity === 0 ? 'bg-red-100 text-red-700' :
                      product.quantity <= 10 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                    }`}>{product.quantity}</span>
                  </td>
                  <td className="py-2 px-3 text-right font-semibold text-green-600">
                    {formatCurrency(parseFloat(product.price) - parseFloat(product.cost_price || 0))}
                  </td>
                </tr>
              ))}
              {filteredProducts.length === 0 && (
                <tr><td colSpan={9} className="py-12 text-center text-gray-400">No products found</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AmazonProductList;